import { utils } from 'near-api-js'
import BigNumber from 'bignumber.js'
import { BROKER_ID } from '@/constants/config'
import {
  generateAction,
  generateTransaction,
  viewMethod,
  callMethod,
  callMethods,
  viewAccount,
  sendBatchTransactions
} from '@/utils/contract'

const NATIVE_TOKEN_SYMBOL = 'NEAR'
const TOKEN_DEPOSIT_GAS = 300000000000000
const TOKEN_WITHDRAW_GAS = 60000000000000
const NATIVE_TOKEN_DEPOSIT_GAS = 60000000000000
const NATIVE_TOKEN_WITHDRAW_GAS = 30000000000000

// Testnet only
const USDC_FAUCET_CONTRACT = 'ft-faucet-usdc.orderly.testnet'

class ContractHelper {
  setConfig(provider, contractId) {
    this.provider = provider || this.provider
    this.contractId = contractId || this.contractId
    this.tokenInfoMap = {}
  }

  setWalletInfo(wallet, accountId) {
    this.wallet = wallet || this.wallet
    this.accountId = accountId || this.accountId
  }

  _viewMethod(method, args = {}) {
    return viewMethod({
      provider: this.provider,
      contractId: this.contractId,
      method,
      args
    })
  }

  _callMethod(method, args = {}, options = {}) {
    // options = { gas, deposit }
    return callMethod({
      provider: this.provider,
      wallet: this.wallet,
      accountId: this.accountId,
      contractId: this.contractId,
      method,
      args,
      ...options
    })
  }

  _callMethods(actions) {
    return callMethods({
      provider: this.provider,
      wallet: this.wallet,
      accountId: this.accountId,
      contractId: this.contractId,
      actions
    })
  }

  _sendBatchTransactions(transactions) {
    return sendBatchTransactions(this.wallet, transactions)
  }

  _viewAccount() {
    return viewAccount(this.provider, this.accountId)
  }

  _generateStorageDepositAction(depositAmount) {
    return generateAction(
      'storage_deposit',
      { account_id: this.accountId },
      undefined,
      depositAmount
    )
  }

  _generateDepositTokenTransaction(tokenContractId, amount) {
    /* token contract requires an attached deposit of at least 1 yoctoNEAR */
    return generateTransaction(tokenContractId, [
      generateAction(
        'ft_transfer_call',
        {
          receiver_id: this.contractId,
          amount,
          msg: '',
          broker_id: BROKER_ID
        },
        TOKEN_DEPOSIT_GAS,
        1
      )
    ])
  }

  _generateWithdrawTokenAction(tokenContractId, amount, gas) {
    return generateAction(
      'user_request_withdraw',
      { token: tokenContractId, amount, broker_id: BROKER_ID },
      gas,
      1
    )
  }

  _generateRequestFreeTokenTransaction() {
    return generateTransaction(USDC_FAUCET_CONTRACT, [
      generateAction('get_tokens', { account_id: this.accountId })
    ])
  }

  checkUserAccountExists() {
    return this._viewMethod('user_account_exists', {
      user: this.accountId
    })
  }

  checkIsOrderlyKeyAnnounced(orderlyKey) {
    return this._viewMethod('is_orderly_key_announced', {
      user: this.accountId,
      orderly_key: orderlyKey
    })
  }

  checkIsTradingKeySet(orderlyKey) {
    return this._viewMethod('is_trading_key_set', {
      user: this.accountId,
      orderly_key: orderlyKey
    })
  }

  getStorageBalanceBounds(tokenContractId = this.contractId) {
    return viewMethod({
      provider: this.provider,
      contractId: tokenContractId,
      method: 'storage_balance_bounds'
    })
  }

  getStorageCostOfAnnounceKey() {
    return this._viewMethod('storage_cost_of_announce_key')
  }

  getStorageBalance(tokenContractId = this.contractId) {
    return viewMethod({
      provider: this.provider,
      contractId: tokenContractId,
      method: 'storage_balance_of',
      args: { account_id: this.accountId }
    })
  }

  getUserStorageUsage() {
    return this._viewMethod('user_storage_usage', {
      user: this.accountId
    })
  }

  getUserStorageStatus(isAccountExists) {
    return isAccountExists
      ? Promise.all([
          this.getStorageBalance(),
          this.getUserStorageUsage()
        ]).then(([storageBalance, storageUsage]) => {
          storageBalance.usage = storageUsage
          return storageBalance
        })
      : { total: 0, available: 0, usage: 0 }
  }

  getStorageCostOfTokenBalance() {
    return this._viewMethod('storage_cost_of_token_balance')
  }

  getOrderlyTradingKey(orderlyKey) {
    return this._viewMethod('get_user_trading_key', {
      user: this.accountId,
      orderly_key: orderlyKey
    })
  }

  getListedTokens() {
    return this._viewMethod('get_listed_tokens')
  }

  getTokenMinimumStorageBalance(tokenContractId) {
    return Promise.all([
      this.getStorageBalance(tokenContractId),
      this.getStorageBalanceBounds(tokenContractId)
    ]).then(([storageBalance, storageBalanceBounds]) => {
      const total = storageBalance?.total || 0
      return BigNumber(total).lt(storageBalanceBounds.min)
        ? BigNumber(storageBalanceBounds.min).minus(total).toFixed()
        : 0
    })
  }

  async getAdditionalStorageCostAction() {
    const [storageBalance, userStorageUsage, storageCostOfTokenBalance] =
      await Promise.all([
        this.getStorageBalance(),
        this.getUserStorageUsage(),
        this.getStorageCostOfTokenBalance()
      ])

    const additionalStorageCost = BigNumber(userStorageUsage)
      .plus(storageCostOfTokenBalance)
      .minus(storageBalance.total)

    return additionalStorageCost.gt(0)
      ? this._generateStorageDepositAction(additionalStorageCost.toFixed())
      : null
  }

  async initialStorageBalance(isAccountExists) {
    const actions = []
    const [storageStatus, storageBalanceBounds, storageCostOfAnnounceKey] =
      await Promise.all([
        this.getUserStorageStatus(isAccountExists),
        this.getStorageBalanceBounds(),
        this.getStorageCostOfAnnounceKey()
      ])

    const minimumCostOfRegistration = storageBalanceBounds.min
    if (BigNumber(storageStatus.total).lt(minimumCostOfRegistration)) {
      const depositAmount = BigNumber(minimumCostOfRegistration)
        .minus(storageStatus.total)
        .toFixed()
      actions.push(this._generateStorageDepositAction(depositAmount))
    }

    const minimumCostOfUsageAndAnnounceKey = BigNumber(storageStatus.usage)
      .plus(storageCostOfAnnounceKey)
      .toNumber()
    if (BigNumber(storageStatus.total).lt(minimumCostOfUsageAndAnnounceKey)) {
      const depositAmount = BigNumber(minimumCostOfUsageAndAnnounceKey)
        .minus(storageStatus.total)
        .toFixed()
      actions.push(this._generateStorageDepositAction(depositAmount))
    }

    return actions.length > 0 ? this._callMethods(actions) : Promise.resolve()
  }

  announceOrderlyKey() {
    return this._callMethod('user_announce_key')
  }

  setOrderlyTradingKey(orderlyTradingKey) {
    return this._callMethod('user_request_set_trading_key', {
      key: orderlyTradingKey
    })
  }

  getWalletNearBalance() {
    return this._viewAccount().then(
      ({ amount }) =>
        (utils.format.formatNearAmount(amount) || '').replaceAll(',', '') || 0
    )
  }

  getTokenMeta(tokenContractId) {
    return viewMethod({
      provider: this.provider,
      contractId: tokenContractId,
      method: 'ft_metadata'
    })
  }

  getWalletTokenBalance(tokenContractId) {
    return viewMethod({
      provider: this.provider,
      contractId: tokenContractId,
      method: 'ft_balance_of',
      args: {
        account_id: this.accountId
      }
    })
  }

  getWalletBalance(tokenList) {
    return tokenList.map(({ token, token_account_id, decimals }) => {
      this.tokenInfoMap[token] = {
        tokenContractId: token_account_id,
        decimals
      }
      return token_account_id === 'near'
        ? this.getWalletNearBalance().then(amount => ({
            token,
            amount,
            decimals: 8
          }))
        : this.getWalletTokenBalance(token_account_id).then(amount => ({
            token,
            decimals,
            amount: BigNumber(amount)
              .div(BigNumber(10).pow(decimals))
              .toNumber()
          }))
    })
  }

  async depositTokens(tokenList, requestFreeToken) {
    const transactions = []
    const assetManagerContractActions = []

    const additionalStorageCostAction =
      await this.getAdditionalStorageCostAction()
    additionalStorageCostAction &&
      assetManagerContractActions.push(additionalStorageCostAction)

    if (requestFreeToken) {
      const { tokenContractId, decimals } = this.tokenInfoMap['USDC.e']
      transactions.push(this._generateRequestFreeTokenTransaction())
      transactions.push(
        this._generateDepositTokenTransaction(
          tokenContractId,
          BigNumber(1000).times(BigNumber(10).pow(decimals)).toFixed()
        )
      )
    }

    tokenList.forEach(({ token, amount }) => {
      const { tokenContractId, decimals } = this.tokenInfoMap[token]
      const isNativeToken = token === NATIVE_TOKEN_SYMBOL
      amount = isNativeToken
        ? utils.format.parseNearAmount(String(amount))
        : BigNumber(amount).times(BigNumber(10).pow(decimals)).toFixed()

      if (isNativeToken) {
        assetManagerContractActions.push(
          generateAction(
            'user_deposit_native_token',
            { broker_id: BROKER_ID },
            NATIVE_TOKEN_DEPOSIT_GAS,
            amount
          )
        )
      } else {
        transactions.push(
          this._generateDepositTokenTransaction(tokenContractId, amount)
        )
      }
    })

    if (assetManagerContractActions.length > 0) {
      transactions.unshift(
        generateTransaction(this.contractId, assetManagerContractActions)
      )
    }
    return this._sendBatchTransactions(transactions)
  }

  async withdrawTokens(tokenList) {
    const actions = []
    const additionalStorageCostAction =
      await this.getAdditionalStorageCostAction()
    additionalStorageCostAction && actions.push(additionalStorageCostAction)

    const storageBalanceCheckTokenList = []
    const storageBalanceCheckTaskList = []

    const withdrawActions = tokenList.map(
      ({ token, amount, checkStorageBalance }) => {
        const { tokenContractId, decimals } = this.tokenInfoMap[token]
        const isNativeToken = token === NATIVE_TOKEN_SYMBOL
        const gas = isNativeToken
          ? NATIVE_TOKEN_WITHDRAW_GAS
          : TOKEN_WITHDRAW_GAS
        if (!isNativeToken && checkStorageBalance) {
          storageBalanceCheckTokenList.push(tokenContractId)
          storageBalanceCheckTaskList.push(
            this.getTokenMinimumStorageBalance(tokenContractId)
          )
        }
        amount = isNativeToken
          ? utils.format.parseNearAmount(String(amount))
          : BigNumber(amount).times(BigNumber(10).pow(decimals)).toFixed()
        return this._generateWithdrawTokenAction(tokenContractId, amount, gas)
      }
    )

    const storageDepositTransactions = []
    await Promise.all(storageBalanceCheckTaskList).then(resultList =>
      resultList.forEach((minimumStorageDeposit, index) => {
        BigNumber(minimumStorageDeposit).gt(0) &&
          storageDepositTransactions.push(
            generateTransaction(storageBalanceCheckTokenList[index], [
              this._generateStorageDepositAction(minimumStorageDeposit)
            ])
          )
      })
    )

    return this._sendBatchTransactions(
      storageDepositTransactions.concat([
        generateTransaction(this.contractId, actions.concat(withdrawActions))
      ])
    )
  }

  settlePnl() {
    return this._callMethod('user_request_settlement', {}, { deposit: 1 })
  }
}

const contractHelper = new ContractHelper()

export default contractHelper
